import { SaleFormInput } from '@src/core/buyerApiClient';
import type { Sale } from '@src/domains/sales';

export interface SaleItemFormInputGroup {
  /** The cart item, and empty while the order has not arrived to place the group. */
  saleItemId: string;
  formInputGroups: Array<SaleFormInput[]>;
}

/**
 * One accordion per cart item, with one row per tariff inside.
 *
 * The link is the tariff, and `formInput.Type` is deliberately not read: the Core packs
 * every input of the product — `per_sale` and `per_tariff` alike — into the same unit
 * group in `genFinalInputList`, so a group is not of one type. Classifying it by its
 * first input places whole units under a sale and loses them.
 */
export const organizeFormInputsBySaleItem = (
  formInputGroups: Array<SaleFormInput[]>,
  sales: Sale[],
): SaleItemFormInputGroup[] => {
  const byItem = new Map<string, SaleItemFormInputGroup>();

  // Indexed once: resolving per form input group would rescan the whole order every time.
  const itemIdByTariff = new Map<string, string>();

  sales.forEach((sale) =>
    sale.items.forEach((item) => item.tariffs.forEach((tariff) => itemIdByTariff.set(tariff.id, item.id))),
  );

  formInputGroups.forEach((inputs) => {
    const first = inputs[0];

    if (!first) {
      return;
    }

    // `SaleFormInput.saleId` is nullable in the contract and comes back empty in practice,
    // so the tariff is the only link: what it cannot place waits for the order.
    const tariffId = first.saleTariff?.id;
    const saleItemId = (tariffId && itemIdByTariff.get(tariffId)) || '';
    const group = byItem.get(saleItemId) ?? { saleItemId, formInputGroups: [] };

    group.formInputGroups.push(inputs);
    byItem.set(saleItemId, group);
  });

  return Array.from(byItem.values());
};
